import { Component, computed, inject, input, model, output, signal, viewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';
import { finalize } from 'rxjs';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { defaultTableDefOptions } from '../../../helpers';
import { LocationGroupService } from '../../../service/location-group.service';
import { LocationGroup } from '../../../types/location-group-response';
import { CellLocationGroupsListActionsComponent } from './cell-locationgroups-list-actions/cell-locationgroups-list-actions.component';
import { CellLocationGroupsListHeaderActionsComponent } from './cell-locationgroups-list-header-actions/cell-locationgroups-list-header-actions.component';

@Component({
  selector: 'app-bl-management-locationgroups',
  templateUrl: './bl-management-locationgroups.component.html',
  styleUrls: ['./bl-management-locationgroups.component.scss'],
  standalone: false,
  providers: [],
})
export class BlManagementLocationGroupsComponent {
  private locationGroupService = inject(LocationGroupService);

  public isLoading = input<boolean>(false);
  public locationGroups = model.required<LocationGroup[]>();
  public selectLocationGroup = output<LocationGroup>();

  public grid = viewChild<AgGridAngular>('locationGroupsGrid');

  public editingLocationGroup = signal<LocationGroup | undefined>(undefined);
  public newLocationGroupModal = viewChild<PanModalComponent>('createNewLocationGroup');
  public newLocationGroupIsSubmitting = signal<boolean>(false);
  public newLocationGroupName = signal<string | undefined>(undefined);
  public newLocationGroupIsNational = signal<boolean>(false);

  public canCreateLocationGroup = computed(
    () => !this.newLocationGroupIsSubmitting() && (this.newLocationGroupName()?.trim().length ?? 0) > 0,
  );

  public readonly tableDef: GridOptions = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 50,
  };

  public readonly locationGroupColumns: ColDef<LocationGroup>[] = [
    {
      field: 'name',
      headerName: 'Location Group',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'is_national',
      headerName: 'National?',
      cellDataType: 'boolean',
      sortable: true,
    },
    {
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 210,
      maxWidth: 210,
      cellClass: ['inline-flex', 'items-center', 'justify-end', 'gap-2'],
      headerComponent: CellLocationGroupsListHeaderActionsComponent,
      headerClass: ['w-full'],
      headerComponentParams: {
        addCallback: () => {
          this.newLocationGroupModal().show();
        },
      },
      cellRenderer: CellLocationGroupsListActionsComponent,
      cellRendererParams: {
        viewCallback: (data: LocationGroup) => {
          this.selectLocationGroup.emit(data);
        },
        editCallback: (data: LocationGroup) => {
          this.editLocationGroup(data);
        },
      },
    },
  ];

  editLocationGroup(locationGroup: LocationGroup) {
    this.editingLocationGroup.set(locationGroup);

    this.newLocationGroupIsNational.set(locationGroup.is_national);
    this.newLocationGroupName.set(locationGroup.name);

    this.newLocationGroupModal().show();
  }

  createLocationGroup() {
    this.newLocationGroupIsSubmitting.set(true);
    this.locationGroupService
      .createLocationGroup({
        name: this.newLocationGroupName(),
        is_national: this.newLocationGroupIsNational(),
      })
      .pipe(
        finalize(() => {
          this.newLocationGroupIsSubmitting.set(false);
          this.newLocationGroupName.set(undefined);
          this.newLocationGroupIsNational.set(false);
          this.newLocationGroupModal().hide();
        }),
      )
      .subscribe((newLocationGroup) => {
        this.locationGroups.update((currentLocationGroups) => [...currentLocationGroups, newLocationGroup]);

        this.grid().api.setFilterModel({
          name: {
            filterType: 'text',
            type: 'contains',
            filter: newLocationGroup.name,
          },
        });
      });
  }

  updateLocationGroup() {
    const group = this.editingLocationGroup();
    if (!group) return;

    this.newLocationGroupIsSubmitting.set(true);

    this.locationGroupService
      .patchLocationGroup(group.id, {
        name: this.newLocationGroupName(),
        is_national: this.newLocationGroupIsNational(),
      })
      .pipe(
        finalize(() => {
          this.newLocationGroupIsSubmitting.set(false);
          this.newLocationGroupName.set(undefined);
          this.newLocationGroupIsNational.set(false);
          this.editingLocationGroup.set(undefined);
          this.newLocationGroupModal().hide();
        }),
      )
      .subscribe((newLocationGroup) => {
        this.locationGroups.update((currentLocationGroups) => {
          const otherLocationGroups = currentLocationGroups.filter((lg) => lg.id !== newLocationGroup.id);
          return [...otherLocationGroups, newLocationGroup];
        });
      });
  }

  clearModalProps() {
    this.newLocationGroupName.set(undefined);
    this.newLocationGroupIsNational.set(false);
    this.editingLocationGroup.set(undefined);
  }
}
